import { useEffect, useState } from 'react'
import { Link, useNavigate, useOutletContext } from 'react-router-dom'

import { createDynamicRule } from '../../../api/dynamicRules.js'
import { listGroupConfigs } from '../../../api/groupConfigs.js'
import {
  buildDynamicRuleElementsPayload,
  createDynamicRuleElement,
  getDynamicRuleElementMode,
  getDynamicRuleTypes,
  getUserParamByKey,
  getUserParamOptions,
  validateDynamicRuleDraft,
} from './dynamicRuleFormUtils.js'

const RULE_TYPES = getDynamicRuleTypes()
const USER_PARAM_OPTIONS = getUserParamOptions()

function renderElementInput(mode, element, onElementChange) {
  if (mode === 'ml') {
    return (
      <input
        className="textInput"
        value={element.leftBoundary}
        onChange={onElementChange(element.id, 'leftBoundary')}
        placeholder="ML model name"
      />
    )
  }

  if (mode === 'cyclic') {
    return (
      <div className="builderRuleGridWide">
        <input
          className="textInput"
          type="number"
          value={element.leftBoundary}
          onChange={onElementChange(element.id, 'leftBoundary')}
          placeholder="Order"
        />
        <input
          className="textInput"
          type="number"
          value={element.rightBoundary}
          onChange={onElementChange(element.id, 'rightBoundary')}
          placeholder="Duration"
        />
        <input
          className="textInput"
          value={element.output}
          onChange={onElementChange(element.id, 'output')}
          placeholder="Output"
        />
      </div>
    )
  }

  if (mode === 'select_single') {
    const option = getUserParamByKey('payer_segmentation')
    return (
      <div className="builderRuleGridSplit">
        <select
          className="textInput"
          value={element.leftBoundary}
          onChange={onElementChange(element.id, 'leftBoundary')}
        >
          <option value="">Select condition</option>
          {(option?.values || []).map((item) => (
            <option key={item.value} value={item.value}>
              {item.label}
            </option>
          ))}
        </select>
        <textarea
          className="textInput textAreaMin70"
          value={element.output}
          onChange={onElementChange(element.id, 'output')}
          placeholder="Output"
        />
      </div>
    )
  }

  if (mode === 'single') {
    return (
      <div className="builderRuleGridSplit">
        <input
          className="textInput"
          value={element.leftBoundary}
          onChange={onElementChange(element.id, 'leftBoundary')}
          placeholder="Condition"
        />
        <textarea
          className="textInput textAreaMin70"
          value={element.output}
          onChange={onElementChange(element.id, 'output')}
          placeholder="Output"
        />
      </div>
    )
  }

  return (
    <div className="builderRuleGridWide">
      <input
        className="textInput"
        value={element.leftBoundary}
        onChange={onElementChange(element.id, 'leftBoundary')}
        placeholder="From"
      />
      <input
        className="textInput"
        value={element.rightBoundary}
        onChange={onElementChange(element.id, 'rightBoundary')}
        placeholder="To (not included)"
      />
      <textarea
        className="textInput textAreaMin70"
        value={element.output}
        onChange={onElementChange(element.id, 'output')}
        placeholder="Output"
      />
    </div>
  )
}

export default function DynamicRuleCreatePage() {
  const { applicationId } = useOutletContext()
  const navigate = useNavigate()

  const [values, setValues] = useState({
    name: '',
    groupConfigId: '',
    ruleType: 'Value',
    userParam: USER_PARAM_OPTIONS[0]?.key || '',
    defaultOutput: '',
  })
  const [elements, setElements] = useState([createDynamicRuleElement()])
  const [groupConfigsState, setGroupConfigsState] = useState({
    status: 'loading',
    items: [],
    error: null,
  })
  const [error, setError] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    let cancelled = false
    setGroupConfigsState({
      status: 'loading',
      items: [],
      error: null,
    })
    listGroupConfigs(applicationId)
      .then((items) => {
        if (cancelled) return
        const sorted = [...(items || [])].sort((a, b) =>
          String(a.name || '').localeCompare(String(b.name || '')),
        )
        setGroupConfigsState({
          status: 'ok',
          items: sorted,
          error: null,
        })
      })
      .catch((fetchError) => {
        if (cancelled) return
        setGroupConfigsState({
          status: 'error',
          items: [],
          error: fetchError,
        })
      })
    return () => {
      cancelled = true
    }
  }, [applicationId])

  function onChange(field) {
    return (e) => {
      const value = field === 'isEnabled' ? e.target.checked : e.target.value
      setValues((prev) => {
        const next = { ...prev, [field]: value }
        if (field === 'ruleType') {
          if (value === 'Cyclic') {
            next.userParam = ''
          } else if (value === 'ETL') {
            next.userParam = prev.ruleType === 'ETL' ? prev.userParam : ''
          } else if (!next.userParam) {
            next.userParam = USER_PARAM_OPTIONS[0]?.key || ''
          }
        }
        return next
      })
    }
  }

  function onElementChange(elementId, field) {
    return (e) => {
      const nextValue = e.target.value
      setElements((prev) =>
        prev.map((item) => (item.id === elementId ? { ...item, [field]: nextValue } : item)),
      )
    }
  }

  function addElement() {
    setElements((prev) => [...prev, createDynamicRuleElement()])
  }

  function removeElement(elementId) {
    setElements((prev) => prev.filter((item) => item.id !== elementId))
  }

  async function handleSubmit(e) {
    e.preventDefault()
    if (isSubmitting) return
    setError(null)
    const name = String(values.name || '').trim()
    if (!name) {
      setError(new Error('Name is required'))
      return
    }
    const groupConfigId = String(values.groupConfigId || '').trim()
    if (!groupConfigId) {
      setError(new Error('Group config is required'))
      return
    }

    const validationError = validateDynamicRuleDraft({
      ruleType: values.ruleType,
      userParam: values.userParam,
      elements,
      defaultOutput: values.defaultOutput,
    })
    if (validationError) {
      setError(new Error(validationError))
      return
    }

    const dynamicRuleElements = buildDynamicRuleElementsPayload({
      ruleType: values.ruleType,
      userParam: values.userParam,
      elements,
      defaultOutput: values.defaultOutput,
    })
    if (dynamicRuleElements.length === 0) {
      setError(new Error('Add at least one valid dynamic rule element.'))
      return
    }

    try {
      setIsSubmitting(true)
      const created = await createDynamicRule(applicationId, {
        groupConfigId: Number(groupConfigId),
        name,
        ruleType: values.ruleType,
        userParam: values.userParam,
        dynamicRuleElements,
      })
      if (created?.id) {
        navigate(`/applications/${applicationId}/dynamic-rules/${created.id}`)
      }
    } catch (err) {
      const detail = err?.body?.detail
      setError(new Error(detail ? String(detail) : String(err?.message || err)))
    } finally {
      setIsSubmitting(false)
    }
  }

  const mode = getDynamicRuleElementMode(values.ruleType, values.userParam)
  const canRemoveElement = elements.length > 1

  return (
    <div className="page">
      <div className="pageHeader">
        <h1 className="pageTitle">Create dynamic rule</h1>
        <Link to={`/applications/${applicationId}/dynamic-rules`} className="button buttonGhost">
          Back to dynamic rules
        </Link>
      </div>

      <section className="card formCard">
        <div className="cardTitle">Dynamic rule</div>
        <form className="formGrid" onSubmit={handleSubmit}>
          <label className="formField">
            <span className="formLabel">Name</span>
            <input className="textInput" value={values.name} onChange={onChange('name')} placeholder="e.g. adtag" />
          </label>

          <label className="formField">
            <span className="formLabel">Type</span>
            <select className="textInput" value={values.ruleType} onChange={onChange('ruleType')}>
              {RULE_TYPES.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </label>

          <label className="formField">
            <span className="formLabel">Group config</span>
            <select className="textInput" value={values.groupConfigId} onChange={onChange('groupConfigId')}>
              <option value="">Select group config</option>
              {groupConfigsState.items.map((groupConfig) => (
                <option key={groupConfig.id} value={String(groupConfig.id)}>
                  {groupConfig.name || `Group #${groupConfig.id}`}
                </option>
              ))}
            </select>
          </label>

          {values.ruleType === 'Cyclic' ? null : values.ruleType === 'ETL' ? (
            <label className="formField">
              <span className="formLabel">ETL table name</span>
              <input
                className="textInput"
                value={values.userParam}
                onChange={onChange('userParam')}
                placeholder="e.g. etl_table_name"
              />
            </label>
          ) : (
            <label className="formField">
              <span className="formLabel">User param</span>
              <select className="textInput" value={values.userParam} onChange={onChange('userParam')}>
                {USER_PARAM_OPTIONS.map((item) => (
                  <option key={item.key} value={item.key}>
                    {item.label}
                  </option>
                ))}
              </select>
            </label>
          )}

          <div className="formField">
            <span className="formLabel">Dynamic rule elements</span>
            <div className="dynamicElementsGrid">
              {elements.map((element) => (
                <div key={element.id} className="dynamicElementRow">
                  {renderElementInput(mode, element, onElementChange)}
                  <button
                    type="button"
                    className="button buttonGhost"
                    disabled={!canRemoveElement}
                    onClick={() => removeElement(element.id)}
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
            <div className="sectionTop10">
              <button type="button" className="button buttonGhost" onClick={addElement}>
                Add element
              </button>
            </div>

            {(mode === 'ml' || mode === 'cyclic') ? null : (
              <div className="sectionTop10">
                <div className="formLabel formLabelBottom6">
                  Default output
                </div>
                <textarea
                  className="textInput textAreaMin90"
                  value={values.defaultOutput}
                  onChange={onChange('defaultOutput')}
                  placeholder="Output for other user parameters"
                />
              </div>
            )}
          </div>

          <div className="formActions">
            <button
              type="submit"
              className="button buttonPrimary"
              disabled={isSubmitting || groupConfigsState.status === 'loading'}
            >
              {isSubmitting ? 'Creating...' : 'Create'}
            </button>
          </div>
        </form>

        {groupConfigsState.status === 'loading' ? (
          <p className="muted inlineMessage">
            Loading group configs...
          </p>
        ) : null}
        {groupConfigsState.status === 'ok' && groupConfigsState.items.length === 0 ? (
          <p className="muted inlineMessage">
            No group configs available. Create a group config first.
          </p>
        ) : null}
        {groupConfigsState.status === 'error' ? (
          <div className="formError">
            {String(groupConfigsState.error?.message || groupConfigsState.error)}
          </div>
        ) : null}
        {error ? <div className="formError">{String(error?.message || error)}</div> : null}
      </section>
    </div>
  )
}
